import type {
	StartWithWithoutGeneric,
	StartWithWithoutTierTwo,
	DropdownInputGroupTierTwo,
	ModuleSchema,
	OptionalSubmodule,
	T2Schema,
} from "@typedefs/modulesInterfaces";
import { FieldType, InputType } from "@typedefs/modulesInterfaces";
import { type ModuleFull, EnergyEntryResponse } from "./energyTypes";
import { getOptions } from "@features/dropdownOptions/fetchOptions";
import { validatePositiveNumber, fuelTypeGrouping } from "../moduleUtils";
import * as Yup from "yup";
import { ENTRY_ENDPOINT } from "@app/features/modules/energyApiSlice";

export const validationSchema = Yup.object({
	energyEntries: Yup.array().of(
		Yup.object().shape({
			fuel_type_start: validatePositiveNumber.required("validations.energy_type_required"),
			fuel_type_w: validatePositiveNumber.required("validations.energy_type_required"),
			fuel_type_wo: validatePositiveNumber.required("validations.energy_type_required"),
			quantity_consumed_per_year_start: validatePositiveNumber.required("validations.energy_consumption_required"),
			quantity_consumed_per_year_w: validatePositiveNumber.required("validations.energy_consumption_required"),
			quantity_consumed_per_year_wo: validatePositiveNumber.required("validations.energy_consumption_required"),
		}),
	),
});

const countries = () => getOptions("country");
const fuelTypes = () => getOptions("fuelTypes");
const efSources = () => getOptions("emissionFactorSources");

const getOptionalSubmodules: () => OptionalSubmodule<EnergyEntryResponse>[] = () => {
	const fuelType: StartWithWithoutGeneric = {
		type: FieldType.SWW_SELECT,
		label: "module.type_of_energy_required",
		inputName: (index: number) => `energyEntries.${index}.fuel_type`,
		startProps: { dropdownOptions: fuelTypes, ...fuelTypeGrouping },
		withProps: { dropdownOptions: fuelTypes, ...fuelTypeGrouping },
		withoutProps: { dropdownOptions: fuelTypes, ...fuelTypeGrouping },
	}
	const quantityConsumedPerYear: StartWithWithoutGeneric = {
		type: FieldType.SWW,
		label: "module.energy_consumption_required",
		inputName: (index: number) => `energyEntries.${index}.quantity_consumed_per_year`,
		startProps: { inputType: "number", labelSuffix: (index: number) => `energyEntries.${index}.fuel_type_start_unit` },
		withProps: { inputType: "number", labelSuffix: (index: number) => `energyEntries.${index}.fuel_type_w_unit` },
		withoutProps: { inputType: "number", labelSuffix: (index: number) => `energyEntries.${index}.fuel_type_wo_unit` },
	}

	return [({
		name: "project.energy",
		inputName: "energyEntries",
		inputGroups: [fuelType, quantityConsumedPerYear],
		repeatable: true,
		linkedTierTwoInputs: ["tiertwo.energy-entries"],
		route: ENTRY_ENDPOINT,
		cacheTag: "EnergyEntriesDefaults",
	})]
}

const getEnergyEntryT2Submodule: () => OptionalSubmodule<EnergyEntryResponse> = () => {


	const efRows: StartWithWithoutTierTwo[] = [
		{
			type: FieldType.SWW_T2,
			inputName: (index: number) => `energyEntries.${index}.energy_ef_co2_t2`,
			label: "module.ef_of_fuel_used_co2",
			unit: "[tCO2-e/unit]",
			links: ["Fuel Type"],
			startProps: { inputType: "number", labelSuffix: (index: number) => `energyEntries.${index}.fuel_type_start_unit` },
			withoutProps: { inputType: "number", labelSuffix: (index: number) => `energyEntries.${index}.fuel_type_wo_unit` },
			withProps: { inputType: "number", labelSuffix: (index: number) => `energyEntries.${index}.fuel_type_w_unit` },
			disableOnElectricity: true,
		},
		{
			type: FieldType.SWW_T2,
			inputName: (index: number) => `energyEntries.${index}.energy_ef_ch4_t2`,
			label: "module.ef_of_fuel_used_ch4",
			unit: "[tCH4/unit]",
			links: ["Fuel Type"],
			startProps: { inputType: "number", labelSuffix: (index: number) => `energyEntries.${index}.fuel_type_start_unit` },
			withoutProps: { inputType: "number", labelSuffix: (index: number) => `energyEntries.${index}.fuel_type_wo_unit` },
			withProps: { inputType: "number", labelSuffix: (index: number) => `energyEntries.${index}.fuel_type_w_unit` },
			disableOnElectricity: true,
		},
		{
			type: FieldType.SWW_T2,
			inputName: (index: number) => `energyEntries.${index}.energy_ef_n2o_t2`,
			label: "module.ef_of_fuel_used_n2o",
			unit: "[tN2O/unit]",
			links: ["Fuel Type"],
			startProps: { inputType: "number", labelSuffix: (index: number) => `energyEntries.${index}.fuel_type_start_unit` },
			withoutProps: { inputType: "number", labelSuffix: (index: number) => `energyEntries.${index}.fuel_type_wo_unit` },
			withProps: { inputType: "number", labelSuffix: (index: number) => `energyEntries.${index}.fuel_type_w_unit` },
			disableOnElectricity: true,
		},
	];

	const electricityEf: StartWithWithoutTierTwo = {
		type: FieldType.SWW_T2,
		label: "module.ef_selected_country",
		unit: "[tCO2/MWh]",
		links: ["Electricity"],
		startProps: { inputType: "number" },
		withProps: { inputType: "number" },
		withoutProps: { inputType: "number" },
		inputName: (index: number) => `energyEntries.${index}.electricity_ef_t2`,
		enableOnlyOnElectricity: true,
	}
	const countryOfOrigin: DropdownInputGroupTierTwo = {
		type: FieldType.SELECT_T2,
		label: "module.power_country_of_origin",
		inputName: (index: number) => `energyEntries.${index}.country_t2`,
		dropdownOptions: countries,
		unit: "",
		links: ["Electricity"],
		defaultValue: "",
	}
	const emissionFactorSources: DropdownInputGroupTierTwo = {
		type: FieldType.SELECT_T2,
		label: "module.ef_source_of_electricity",
		inputName: (index: number) => `energyEntries.${index}.ef_source`,
		dropdownOptions: efSources,
		unit: "",
		links: ["Electricity"],
		defaultValue: "",
		hideDefaultLabel: true,
	}
	const transmissionLoss: StartWithWithoutTierTwo = {
		type: FieldType.SWW_T2,
		inputName: (index: number) => `energyEntries.${index}.transmission_loss_t2`,
		label: "module.transmission_loss",
		unit: "[%]",
		links: ["Electricity"],
		startProps: { inputType: "number", defaultValue: 0 },
		withoutProps: { inputType: "number", defaultValue: 0 },
		withProps: { inputType: "number", defaultValue: 0 },
		inputType: InputType.PERCENTAGE,
		enableOnlyOnElectricity: true,
	}


	return {
		name: "project.energy",
		inputName: "energyEntries",
		inputGroups: [...efRows],
		route: ENTRY_ENDPOINT,
		cacheTag: "EnergyEntriesDefaults",
		conditionalSections: [
			{
				inputGroups: [electricityEf, countryOfOrigin, emissionFactorSources, transmissionLoss],
				conditionName: "is_electricity_selected"
			}
		]
	}
}

export const getSchemas = () => {
	const moduleSchema: ModuleSchema = {
		initInputGroups: [],
		mandatorySubmodules: [],
		optionalSubmodules: getOptionalSubmodules(),
	};

	const t2EntrySchema = getEnergyEntryT2Submodule();
	const t2Schema: T2Schema = {
		submodules: [],
		optionalSubmodules: [t2EntrySchema]
	};


	return {
		moduleSchema,
		t2Schema,
	};
};

export const initialValues: ModuleFull = {
	energyEntries: [],
	parent: {
		id: null,
		note: null,
		activity: null,
		start_year: null,
		soc_t2_start: null,
		soc_t2_w: null,
		soc_t2_wo: null,
		status: null,
	},
};
